import {Box, HBox} from '../../../components/core/view';
import {ChevronDownIcon, ChevronUpIcon, IconButton} from 'native-base';
import {
  DeliveryLocationStatus,
  GroupedAssignedItems,
  OrderItems,
  RunForBatchDetail,
} from '../../../batchDetails/types';
import {OrderItemStatus, getClientType} from '@buncha/common/types/common';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useColorThemeType, useFontTheme} from '../../../appStyles/hooks/theme';

import {Accordian} from '../../../components/composites';
import AddToCartIcon from '../../../components/core/icon/AddToCartIcon';
import {Analytics} from '@buncha/config/analytics/analytics';
import {BottomButton} from '@buncha/components/composites/Button';
import {CustomText} from '../../../components/core/text';
import CustomerOrderInfo from '../CustomerOrderInfo';
import {EventType} from '@buncha/config/analytics/type';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import PackOrderItemList from '../PackOrderItemList';
import {ShopperScreens} from '@buncha/home/navigation/types';
import SpinnerComponent from '@buncha/components/core/loader/SpinnerComponent';
import UsedBagBottomSheet from '../VanLoad/UsedBag/UsedBagBottomSheet';
import {styles} from './style';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';

interface PackItemAccordionProps {
  item: GroupedAssignedItems;
  changedItemStatus: (
    orderItem: OrderItems,
    status: OrderItemStatus,
    lastItem: boolean,
  ) => void;
  packingFinished: boolean;
  orderLoading: boolean;
  runDetail?: RunForBatchDetail;
  isActive: boolean;
  itemsPackedCount: (orderItems: OrderItems[]) => number;
  itemsUnPackedCount: (orderItems: OrderItems[]) => number;
  handleMarkItemsAsPacked: (orderId: number) => void;
  toggleItem: () => void;
  onRefresh: () => void;
  orderDeliveredStatus: boolean;
  needRefresh: boolean;
  navigateToAddItem: (item: GroupedAssignedItems) => void;
}

function PackItemAccordion(props: PackItemAccordionProps) {
  const {
    item,
    changedItemStatus,
    packingFinished,
    orderLoading,
    runDetail,
    isActive,
    itemsPackedCount,
    itemsUnPackedCount,
    handleMarkItemsAsPacked,
    toggleItem,
    orderDeliveredStatus,
    needRefresh,
    navigateToAddItem,
  } = props;
  const buyer = item?.metaData?.buyer;
  const order = item?.metaData?.order;
  const isVip = buyer?.isVip;
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const {body} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};

  const isMeijer = useIsMeijerStore(runDetail?.store?.id);
  const storeIndex = runDetail?.store?.elasticSearchIndex || '';

  const [open, setOpen] = useState(false);

  const toggleBottomSheet = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const orderPackedStatus =
    order.deliveryLocation?.deliveryStatus ===
    DeliveryLocationStatus.orderPacked;
  const itemsPacked = itemsPackedCount(item.orderItems);
  const itemsUnPacked = itemsUnPackedCount(item.orderItems);

  const handleAddItem = useCallback(
    (group: GroupedAssignedItems) => {
      const orderId = group.orderItems?.length
        ? group.orderItems[0].orderId
        : 0;
      Analytics.eventWithProps('Addition Item', {
        type: EventType.cta,
        runId: runDetail?.id,
        client: getClientType(isMeijer),
        orderId,
        source: 'Packing Screen',
      });
      navigateToAddItem(item);
    },
    [isMeijer, item, navigateToAddItem, runDetail?.id],
  );

  const checkLastItemPick = useMemo(() => {
    if (item.orderItems) {
      const data = item.orderItems.find(
        or =>
          or.itemStatus === OrderItemStatus.notPacked && !or.isAdjacentOOSItem,
      );

      if (data) return false;
      return true;
    }
    return false;
  }, [item.orderItems]);

  const buttonTitle = useMemo(() => {
    if (orderPackedStatus || packingFinished)
      return body?.itemStatus?.[0]?.replace('{itemsPacked}', itemsPacked);
    if (!itemsUnPacked)
      if (item?.metaData?.firstShopperName) return body?.itemStatus?.[1];
      else return body?.itemStatus?.[0]?.replace('{itemsPacked}', itemsPacked);
    if (itemsPacked)
      return body?.itemStatus?.[2]
        ?.replace('{itemsPacked}', itemsPacked)
        ?.replace('{itemsUnPacked}', itemsUnPacked);
    return body?.itemStatus?.[3]?.replace('{itemsUnPacked}', itemsUnPacked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    itemsPacked,
    orderDeliveredStatus,
    orderPackedStatus,
    packingFinished,
    order,
  ]);

  const buttonDisabledCondition = useMemo(() => {
    if (needRefresh) return true;
    if (item?.metaData?.firstShopperName) return true;
    if (orderPackedStatus || packingFinished) return true;
    return !checkLastItemPick || orderLoading;
  }, [
    checkLastItemPick,
    item?.metaData?.firstShopperName,
    needRefresh,
    orderLoading,
    orderPackedStatus,
    packingFinished,
  ]);

  useEffect(() => {
    if (orderPackedStatus && !order?.bagsUsed && checkLastItemPick && !isMeijer)
      setOpen(true);
    else setOpen(false);
  }, [orderPackedStatus, order?.bagsUsed, checkLastItemPick, isMeijer]);

  return (
    <Box>
      <Box style={[styles.mainContainer]}>
        <Accordian
          isExpanded={isActive}
          header={
            <HBox style={[styles.mainHeaderContainer]}>
              <CustomerOrderInfo
                groupedAssignedItems={item}
                isVip={isVip}
                isPackingScreen={true}
                itemsPacked={itemsPacked}
                adjShopperDetails={item?.metaData?.firstShopperName}
              />
              <HBox>
                <If condition={!item?.metaData?.firstShopperName}>
                  <IconButton
                    onPress={() => handleAddItem(item)}
                    isDisabled={orderPackedStatus || packingFinished}>
                    <AddToCartIcon width={18} height={18} viewBox="0 0 21 21" />
                  </IconButton>
                </If>
                <Box
                  style={[
                    styles.countBox,
                    {
                      backgroundColor: theme?.background.gray[200],
                    },
                  ]}>
                  <CustomText
                    style={[
                      {
                        fontFamily: muliFont[700].normal,
                        color: theme?.text.gray[100],
                      },
                    ]}>
                    {item.orderItems.length}
                  </CustomText>
                </Box>
              </HBox>
            </HBox>
          }
          iconEnable={<ChevronUpIcon color={theme?.text.blue[300]} />}
          iconDisable={<ChevronDownIcon color={theme?.text.blue[300]} />}
          onPress={toggleItem}>
          <Box>
            <PackOrderItemList
              packOrderItem={item.orderItems}
              onClick={changedItemStatus}
              packingFinished={orderPackedStatus || packingFinished}
              orderLoading={orderLoading}
              storeIndex={storeIndex}
              adjacentShopperDetails={item?.metaData?.firstShopperName}
              isMeijer={isMeijer}
            />
            <If condition={!item?.metaData?.firstShopperName}>
              <BottomButton
                title={buttonTitle}
                disabled={buttonDisabledCondition}
                bgColor={theme?.background.blue[100]}
                onPress={() => handleMarkItemsAsPacked(order.id)}
                isLoading={orderLoading}
                loadingIndicator={<SpinnerComponent />}
              />
            </If>
          </Box>
        </Accordian>
      </Box>
      <If condition={open}>
        <UsedBagBottomSheet
          open={open || false}
          onClose={toggleBottomSheet}
          buyerName={`${buyer?.firstName} ${buyer?.lastName}`}
          usedBag={0}
          orderId={item?.metaData.orderId}
          item={item}
          onRefresh={props.onRefresh}
          runId={runDetail?.id}
          isB2BStore={isMeijer}
        />
      </If>
    </Box>
  );
}

export default React.memo(PackItemAccordion);
